import type {
	BulkUpdateCurrentLevelsResponseJSON,
	ExportCompetencyAssignmentsCsvResponse,
	ListAssignmentHistoryResponseJSON,
	ListCompanyCompetencyAssignmentsResponseJSON,
	ListCompetencyAssignmentsResponseJSON,
	ListTeamCompetencyAssignmentsResponseJSON,
	UpdateCurrentLevelResponseJSON,
} from "@performyard/service-contracts";

import request from "./internal-api/internal-base-request";

export type CompetencyAssignmentSortBy = "competency_name" | "expected_level" | "current_level";
export type CompetencyAssignmentSourceFilter = "any" | "role" | "company_wide";
export type CompetencyAssignmentStatusFilter = "any" | "active" | "archived";
export type CompetencyLevelGap = "exceeds" | "meets" | "below" | "no_level";

export type ListCompetencyAssignmentsParams = {
	employeeId?: string;
	search?: string;
	source?: CompetencyAssignmentSourceFilter;
	status?: CompetencyAssignmentStatusFilter;
	levelGap?: CompetencyLevelGap[];
	sortBy?: CompetencyAssignmentSortBy;
	sortOrder?: "asc" | "desc";
	limit?: number;
	skip?: number;
};

export type ListAssignmentHistoryParams = {
	limit?: number;
	skip?: number;
};

// Note: role_name not currently sortable in the backend
export type TeamCompetencyAssignmentSortBy = "assignee_name" | "competency_name" | "expected_level" | "current_level";

// The company (org-wide) list sorts by the same columns as the team list.
export type CompanyCompetencyAssignmentSortBy = TeamCompetencyAssignmentSortBy;

export type ListTeamCompetencyAssignmentsParams = {
	employeeId?: string;
	search?: string;
	source?: CompetencyAssignmentSourceFilter;
	status?: CompetencyAssignmentStatusFilter;
	levelGap?: CompetencyLevelGap[];
	assigneeId?: string;
	competencyId?: string;
	roleIds?: string[];
	sortBy?: TeamCompetencyAssignmentSortBy;
	sortOrder?: "asc" | "desc";
	limit?: number;
	skip?: number;
};

// GET /v1/competency-assignments
export const getCompetencyAssignments = async (
	params: ListCompetencyAssignmentsParams = {}
): Promise<ListCompetencyAssignmentsResponseJSON> => {
	const searchParams = new URLSearchParams();
	if (params.employeeId) searchParams.set("employeeId", params.employeeId);
	if (params.search) searchParams.set("search", params.search);
	if (params.source) searchParams.set("source", params.source);
	if (params.status) searchParams.set("status", params.status);
	if (params.levelGap?.length) params.levelGap.forEach((gap) => searchParams.append("levelGap", gap));
	if (params.sortBy) searchParams.set("sortBy", params.sortBy);
	if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
	if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
	if (params.skip !== undefined) searchParams.set("skip", String(params.skip));

	const query = searchParams.toString();
	const [response] = await request<ListCompetencyAssignmentsResponseJSON>(
		`/v1/competency-assignments${query ? `?${query}` : ""}`,
		{ method: "GET" }
	);
	return response;
};

// GET /v1/competency-assignments/:id/history
export const getAssignmentHistory = async (
	assignmentId: string,
	params: ListAssignmentHistoryParams = {}
): Promise<ListAssignmentHistoryResponseJSON> => {
	const searchParams = new URLSearchParams();
	if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
	if (params.skip !== undefined) searchParams.set("skip", String(params.skip));

	const query = searchParams.toString();
	const [response] = await request<ListAssignmentHistoryResponseJSON>(
		`/v1/competency-assignments/${assignmentId}/history${query ? `?${query}` : ""}`,
		{ method: "GET" }
	);
	return response;
};

// GET /v1/competency-assignments/team
export const getTeamCompetencyAssignments = async (
	params: ListTeamCompetencyAssignmentsParams = {}
): Promise<ListTeamCompetencyAssignmentsResponseJSON> => {
	const searchParams = new URLSearchParams();
	if (params.employeeId) searchParams.set("employeeId", params.employeeId);
	if (params.search) searchParams.set("search", params.search);
	if (params.source) searchParams.set("source", params.source);
	if (params.status) searchParams.set("status", params.status);
	if (params.levelGap?.length) params.levelGap.forEach((gap) => searchParams.append("levelGap", gap));
	if (params.assigneeId) searchParams.set("assigneeId", params.assigneeId);
	if (params.competencyId) searchParams.set("competencyId", params.competencyId);
	if (params.roleIds?.length) params.roleIds.forEach((id) => searchParams.append("roleIds", id));
	if (params.sortBy) searchParams.set("sortBy", params.sortBy);
	if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
	if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
	if (params.skip !== undefined) searchParams.set("skip", String(params.skip));

	const query = searchParams.toString();
	const [response] = await request<ListTeamCompetencyAssignmentsResponseJSON>(
		`/v1/competency-assignments/team${query ? `?${query}` : ""}`,
		{ method: "GET" }
	);
	return response;
};

export type ListCompanyCompetencyAssignmentsParams = {
	search?: string;
	source?: CompetencyAssignmentSourceFilter;
	status?: CompetencyAssignmentStatusFilter;
	levelGap?: CompetencyLevelGap[];
	assigneeId?: string;
	managerId?: string;
	competencyIds?: string[];
	roleIds?: string[];
	sortBy?: CompanyCompetencyAssignmentSortBy;
	sortOrder?: "asc" | "desc";
	limit?: number;
	skip?: number;
};

// GET /v1/competency-assignments/company — org-wide (admin) read; competencyIds is multi,
// unlike the team route's singular competencyId.
export const getCompanyCompetencyAssignments = async (
	params: ListCompanyCompetencyAssignmentsParams = {}
): Promise<ListCompanyCompetencyAssignmentsResponseJSON> => {
	const searchParams = new URLSearchParams();
	if (params.search) searchParams.set("search", params.search);
	if (params.source) searchParams.set("source", params.source);
	if (params.status) searchParams.set("status", params.status);
	if (params.levelGap?.length) params.levelGap.forEach((gap) => searchParams.append("levelGap", gap));
	if (params.assigneeId) searchParams.set("assigneeId", params.assigneeId);
	if (params.managerId) searchParams.set("managerId", params.managerId);
	if (params.competencyIds?.length)
		params.competencyIds.forEach((competencyId) => searchParams.append("competencyIds", competencyId));
	if (params.roleIds?.length) params.roleIds.forEach((roleId) => searchParams.append("roleIds", roleId));
	if (params.sortBy) searchParams.set("sortBy", params.sortBy);
	if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
	if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
	if (params.skip !== undefined) searchParams.set("skip", String(params.skip));

	const query = searchParams.toString();
	const [response] = await request<ListCompanyCompetencyAssignmentsResponseJSON>(
		`/v1/competency-assignments/company${query ? `?${query}` : ""}`,
		{ method: "GET" }
	);
	return response;
};

export type RequestCompetencyAssignmentsCsvParams = {
	scope: "employee" | "team" | "company";
	employeeId?: string;
	search?: string;
	source?: CompetencyAssignmentSourceFilter;
	status?: CompetencyAssignmentStatusFilter;
	levelGap?: CompetencyLevelGap[];
	assigneeId?: string;
	managerId?: string;
	competencyIds?: string[];
	roleIds?: string[];
	sortBy?: CompetencyAssignmentSortBy | TeamCompetencyAssignmentSortBy;
	sortOrder?: "asc" | "desc";
};

// GET /v1/competency-assignments/csv — dispatches an async export; returns the task id to
// drive the SSE download (the querystring mirrors the list params so the export matches the view).
export const requestCompetencyAssignmentsCsv = async (
	params: RequestCompetencyAssignmentsCsvParams
): Promise<ExportCompetencyAssignmentsCsvResponse> => {
	const searchParams = new URLSearchParams();
	searchParams.set("scope", params.scope);
	if (params.employeeId) searchParams.set("employeeId", params.employeeId);
	if (params.search) searchParams.set("search", params.search);
	if (params.source) searchParams.set("source", params.source);
	if (params.status) searchParams.set("status", params.status);
	if (params.levelGap?.length) params.levelGap.forEach((gap) => searchParams.append("levelGap", gap));
	if (params.assigneeId) searchParams.set("assigneeId", params.assigneeId);
	if (params.managerId) searchParams.set("managerId", params.managerId);
	if (params?.competencyIds?.length)
		params.competencyIds.forEach((competencyId) => searchParams.append("competencyIds", competencyId));
	if (params.roleIds?.length) params.roleIds.forEach((id) => searchParams.append("roleIds", id));
	if (params.sortBy) searchParams.set("sortBy", params.sortBy);
	if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);

	const [response] = await request<ExportCompetencyAssignmentsCsvResponse>(
		`/v1/competency-assignments/csv?${searchParams.toString()}`,
		{ method: "GET" }
	);
	return response;
};

// PATCH /v1/competency-assignments/:id/current-level
export const updateAssignmentCurrentLevel = async (
	assignmentId: string,
	currentLevel: number | null
): Promise<UpdateCurrentLevelResponseJSON> => {
	const [response] = await request<UpdateCurrentLevelResponseJSON>(
		`/v1/competency-assignments/${assignmentId}/current-level`,
		{ method: "PATCH", body: JSON.stringify({ current_level: currentLevel }) }
	);
	return response;
};

export type BulkCurrentLevelUpdate = { assignment_id: string; current_level: number | null };

// PATCH /v1/competency-assignments/current-levels — bulk current-level update (inline edit mode);
// partial success — the response's failed[] carries per-item authz/validation errors.
export const bulkUpdateAssignmentCurrentLevels = async (
	updates: BulkCurrentLevelUpdate[]
): Promise<BulkUpdateCurrentLevelsResponseJSON> => {
	const [response] = await request<BulkUpdateCurrentLevelsResponseJSON>(`/v1/competency-assignments/current-levels`, {
		method: "PATCH",
		body: JSON.stringify({ updates }),
	});
	return response;
};
